<template>
  <view-wrapper class="space-y-6">
    <page-title
      data-cy="custom-webhooks-title"
      :title="t('CUSTOM_WEBHOOKS.TITLE', 2)"
      :description="t('CUSTOM_WEBHOOKS.MANAGE', 2)"
    >
      <template #action>
        <div class="flex h-10 space-x-4">
          <div>
            <search-input v-model="filters.search" no-padding />
          </div>
          <primary-button @click="handleNewClick">
            {{ $t("CUSTOM_WEBHOOKS.NEW") }}
          </primary-button>
        </div>
      </template>
    </page-title>
    <div>
      <loader :is-loading="loading" />
      <div class="relative">
        <lf-table
          :columns="columns"
          :data="customWebhooks.data || []"
          :loading="loading"
          :select-all="showSelectAll"
          :has-select-mode-option="false"
          :is-select-mode-enabled="!showSelectAll"
          :show-multiselect-controls="showSelectAll"
          row-is-clickable
          @row-clicked="openWebhook"
          @multiselect:change="handleMultiselectChange"
          @selected-rows="handleSelectedRows"
        />
        <lf-pagination
          v-if="customWebhooks.meta"
          :metadata="customWebhooks.meta"
          show-per-page
          @change-page="changePage"
          @change-items-per-page="changePerPage"
        />
      </div>
    </div>
  </view-wrapper>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import compact from "lodash/compact";
import { storeToRefs } from "pinia";

import { stripHTMLTagsFromString } from "@/helpers/common";
import { DEFAULT_PER_PAGE } from "@/helpers/constants";
import { truncateWithEllipsis } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import {
  ROUTE_CUSTOM_WEBHOOKS_EDIT,
  ROUTE_CUSTOM_WEBHOOKS_NEW,
} from "@/router/routes";
import customWebhookApiService from "@/services/modules/customWebhook";
import { useCustomWebhookStore } from "@/stores/customWebhook";

import TemplateName from "@/components/communicationTemplates/TemplateName.vue";
import TemplateOrganizationBadge from "@/components/communicationTemplates/TemplateOrganizationBadge.vue";
import TemplateUser from "@/components/communicationTemplates/TemplateUser.vue";
import CustomWebhooksActionMenu from "@/components/customWebhooks/CustomWebhooksActionMenu.vue";
import ClickToCopyIdColumn from "@/components/ui/ClickToCopyIdColumn.vue";
import PageTitle from "@/components/ui/PageTitle.vue";
import LfPagination from "@/components/ui/table/LfPagination.vue";
import LfTable from "@/components/ui/table/LfTable.vue";
import DateAndTimeDisplay from "@/components/ui/text/DateAndTimeDisplay.vue";

import type { IDealsColumn } from "@/models/applications";
import type { TemplateViewParams } from "@/models/clients";
import type { IClient } from "@/models/clients";
import type { Nullish } from "@/models/common";
import type { CustomWebhook } from "@/models/customWebhook";

const MAX_TEXT_LENGTH = 50;

const router = useRouter();
const { t } = useI18n();
const store = useCustomWebhookStore();
const { getters } = useStore();
const { isLendflowUser } = useAuth();

const showSelectAll = ref(false);
const selectedIds = ref<number[]>([]);
const { customWebhooks } = storeToRefs(store);

const activeClient = computed<IClient | null>(() => getters["clients/active"]);
const activeClientId = computed(() => activeClient.value?.id);

const filters = ref<TemplateViewParams>({
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  search: "",
  client_id: activeClientId.value,
});

const truncatedFormatter = (value: string | Nullish) => {
  if (typeof value !== "string") {
    return "";
  }
  return truncateWithEllipsis(
    stripHTMLTagsFromString(value, false),
    MAX_TEXT_LENGTH
  );
};

const refreshCustomWebhooks = async () => {
  await getCustomWebhooks(filters.value);
};

const editWebhook = (id: string) => {
  openWebhook(id);
};

const deleteWebhook = async (id: string) => {
  await customWebhookApiService.deleteCustomWebhook(id);
  await refreshCustomWebhooks();
};

const columns: IDealsColumn[] = compact([
  {
    label: t("COMMON.WEBHOOK_NAME"),
    key: "name",
    component: TemplateName,
  },
  {
    label: t("COMMON.DESCRIPTION"),
    key: "description",
    class: "max-w-52 line-clamp-2",
    formatter: truncatedFormatter,
  },
  {
    label: t("COMMON.WEBHOOK_ID"),
    key: "id",
    component: ClickToCopyIdColumn,
    formatModel: true,
    formatter: (row) => String(row.id).substring(0, 5),
  },
  isLendflowUser && {
    label: t("ORGANIZATION.TITLE"),
    key: "client.name",
    component: TemplateOrganizationBadge,
  },
  {
    label: t("COMMON.CREATED_BY"),
    key: "user",
    component: TemplateUser,
  },
  {
    label: t("COMMON.DATE_UPDATED"),
    key: "updated_at",
    component: DateAndTimeDisplay,
  },
  {
    label: t("COMMON.ACTIONS"),
    key: "",
    component: CustomWebhooksActionMenu,
    eventHandlers: {
      "webhook:edit": editWebhook,
      "webhook:delete": deleteWebhook,
    },
  },
]);

const {
  fetchWrapper: getCustomWebhooks,
  loading,
  changePage,
  changePerPage,
} = usePromiseWrapper(
  async (newFilters) => {
    const payload = { ...newFilters };
    if (payload.search === "") {
      delete payload.search;
    }
    if (activeClientId.value) {
      payload.client_id = activeClientId.value;
    }
    await store.getCustomWebhooks(payload);
  },
  { filters }
);

const handleMultiselectChange = (value: boolean) => {
  showSelectAll.value = !value;
  selectedIds.value = [];
};

const handleSelectedRows = (selectedRows: CustomWebhook["id"][]) => {
  selectedIds.value = selectedRows as number[];
};

const handleNewClick = () => {
  router.push({ name: ROUTE_CUSTOM_WEBHOOKS_NEW });
};

const openWebhook = async (id: string) => {
  if (!id) {
    return;
  }
  const pushPayload = {
    name: ROUTE_CUSTOM_WEBHOOKS_EDIT,
    params: {
      templateId: id,
    },
  };

  await router.push(pushPayload);
};

watch(
  filters.value,
  (newValue) => {
    getCustomWebhooks(newValue);
  },
  { deep: true, immediate: true }
);

onUnmounted(() => {
  store.unsetCustomWebhooks();
});
</script>
